import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useWatch } from "react-hook-form";

import type { CommunityGroupV2Dto, ConstraintListItemDto } from "@/api/types";
import { AudiencePreview } from "@/components/AudienceSelector/AudiencePreview";
import { Drawer } from "@/components/Drawer/Drawer";
import { AnnotationInfoIcon } from "@/components/Icons/AnnotationInfoIcon";
import { Button } from "@/design-system/Button";
import { Checkbox } from "@/design-system/Checkbox";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { MultiSelect } from "@/design-system/MultiSelect";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { flattenAudience, getIconByAudienceType, mapAudienceByType, orderAudienceTypes } from "@/helpers/audience";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useGeneralQueries } from "@/queries/general/queryOptions";
import { useTranslation } from "@/translations";

import type { CreateOrEditCommunityGroupFormValues } from "./community-group-editor/CommunityGroupForm";

type AudienceType = ConstraintListItemDto["type"];

type GroupAudienceSelectorProps = {
  isEditMode: boolean;
  groupType: Omit<CommunityGroupV2Dto["type"], "realEstate">;
  onSave: ({
    audience,
    isRealEstateGroup,
    isAllResidentsAdded,
  }: {
    audience: ConstraintListItemDto[];
    isRealEstateGroup: boolean;
    isAllResidentsAdded: boolean;
  }) => void;
};

export function GroupAudienceSelector({ isEditMode, groupType, onSave }: GroupAudienceSelectorProps): React.ReactNode {
  const [isDrawerOpened, drawerHandler] = useBool();

  const defaultAudience = useWatch<CreateOrEditCommunityGroupFormValues, "audience">({ name: "audience" });
  const defaultAudienceGroupByType = mapAudienceByType(defaultAudience);
  const defaultIsRealEstateGroup = Boolean(
    useWatch<CreateOrEditCommunityGroupFormValues, "isRealEstate">({ name: "isRealEstate" }),
  );

  const [currAudience, setCurrAudience] =
    useState<Record<AudienceType, ConstraintListItemDto[]>>(defaultAudienceGroupByType);
  const [isRealEstateGroup, setIsRealEstateGroup] = useState(defaultIsRealEstateGroup);
  const [isAllResidentsAdded, setIsAllResidentsAdded] = useState(false);

  const { t } = useTranslation();
  const generalQueries = useGeneralQueries();
  const {
    data: constraints,
    isPending: isPendingConstraints,
    isError: isErrorConstraints,
  } = useQuery(generalQueries.constraints());
  const sessionUser = useSessionUser();

  useEffect(() => {
    if (isRealEstateGroup && sessionUser.isSuperAdmin) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setIsAllResidentsAdded(true);
    }
  }, [isRealEstateGroup, sessionUser.isSuperAdmin]);

  const onChangeAudienceItems = (audienceType: AudienceType, items: ConstraintListItemDto[]) => {
    setCurrAudience((prevValue) => ({ ...prevValue, [audienceType]: items }));
  };

  const onClickClose = () => {
    setCurrAudience(defaultAudienceGroupByType);
  };

  const onClickSave = () => {
    onSave({
      audience: flattenAudience(currAudience),
      isRealEstateGroup: isRealEstateGroup,
      isAllResidentsAdded,
    });
  };

  const availableAudience = useMemo(() => mapAudienceByType(constraints?.items || []), [constraints?.items]);
  const availableAudienceTypes = Object.keys(availableAudience) as AudienceType[];
  const configTexts = {
    apartmentType: {
      title: t("model.constraint.address"),
      description: t("component.audience-selector.general-description", {
        audienceTarget: t("common.entity.asset").toLowerCase(),
        audienceEntity: t("model.constraint.type.apartment-type").toLowerCase(),
      }),
      placeholder: t("component.audience-selector.general-placeholder", {
        audienceEntity: t("model.constraint.type.apartment-type").toLowerCase(),
      }),
    },
    floor: {
      title: t("model.constraint.floor"),
      description: t("component.audience-selector.general-description", {
        audienceTarget: t("common.entity.asset").toLowerCase(),
        audienceEntity: t("model.constraint.type.floor").toLowerCase(),
      }),
      placeholder: t("component.audience-selector.general-placeholder", {
        audienceEntity: t("model.constraint.type.floor").toLowerCase(),
      }),
    },
    building: {
      title: t("model.constraint.building"),
      description: t("component.audience-selector.general-description", {
        audienceTarget: t("common.entity.asset").toLowerCase(),
        audienceEntity: t("model.constraint.type.building").toLowerCase(),
      }),
      placeholder: t("component.audience-selector.general-placeholder", {
        audienceEntity: t("model.constraint.type.building").toLowerCase(),
      }),
    },
    company: {
      title: t("model.constraint.company"),
      description: t("component.audience-selector.general-description", {
        audienceTarget: t("common.entity.asset").toLowerCase(),
        audienceEntity: t("model.constraint.type.company").toLowerCase(),
      }),
      placeholder: t("component.audience-selector.general-placeholder", {
        audienceEntity: t("model.constraint.type.company").toLowerCase(),
      }),
    },
  };

  // Hide audience selector if there are no constraints available (aka no address)
  if (!isPendingConstraints && !isErrorConstraints && constraints.items.length === 0) {
    return null;
  }

  return (
    <>
      <AudiencePreview audience={defaultAudience} onClick={drawerHandler.setTrue} truncate />
      <Drawer.Root
        title={t("component.audience-selector.title")}
        isOpened={isDrawerOpened}
        onOpenChange={drawerHandler.set}
      >
        {isPendingConstraints && <FullSizeLoader />}
        {!isPendingConstraints && !isErrorConstraints && (
          <Drawer.Body>
            <Stack gap="md">
              {isEditMode && (
                <Notice
                  icon={<AnnotationInfoIcon />}
                  variant="warning"
                  message={t("component.audience-selector.edit-warning.group")}
                />
              )}
              <Stack data-testid="audience-root" gap="md">
                {/* Order audience sections by a specific ordering */}
                {orderAudienceTypes(availableAudienceTypes).map((audienceType) => {
                  const icon = getIconByAudienceType(audienceType);

                  return (
                    <Stack key={audienceType} data-testid="audience-item" gap="xs">
                      <Inline gap="xs" align="center">
                        {icon}
                        <h2 className="text-heading-4">{configTexts[audienceType].title}</h2>
                      </Inline>
                      <p className="text-text-secondary">{configTexts[audienceType].description}</p>
                      <MultiSelect<ConstraintListItemDto>
                        selected={currAudience[audienceType]}
                        placeholder={configTexts[audienceType].placeholder}
                        items={availableAudience[audienceType]}
                        keySelector={({ id }) => id}
                        renderOption={({ key }) => key}
                        onChange={(items) => onChangeAudienceItems(audienceType, items)}
                      />
                    </Stack>
                  );
                })}
              </Stack>
              {groupType === "interest" && (
                <FormField
                  label={t("page.community-groups.create-or-edit.type.real-estate")}
                  tooltip={t("page.community-groups.create-or-edit.type.real-estate.tooltip")}
                >
                  <Checkbox
                    label={t("page.community-groups.create-or-edit.type.real-estate")}
                    checked={isRealEstateGroup}
                    disabled={isEditMode}
                    onChange={(e) => setIsRealEstateGroup(e.target.checked)}
                  />
                </FormField>
              )}
              {!isEditMode && sessionUser.isSuperAdmin && (
                <Checkbox
                  label={t("page.community-groups.create-or-edit.constraint.add-all-residents")}
                  checked={isAllResidentsAdded}
                  disabled={isRealEstateGroup}
                  onChange={(e) => setIsAllResidentsAdded(e.target.checked)}
                />
              )}

              <footer className="mt-space-lg flex w-full items-center justify-end gap-space-sm">
                <Drawer.Close>
                  <Button variant="subtle" tone="primary" onClick={onClickClose}>
                    {t("common.action.cancel")}
                  </Button>
                </Drawer.Close>
                <Drawer.Close>
                  <Button variant="filled" onClick={onClickSave}>
                    {t("common.action.set")}
                  </Button>
                </Drawer.Close>
              </footer>
            </Stack>
          </Drawer.Body>
        )}
      </Drawer.Root>
    </>
  );
}
